import { DentistItemSlot, PatientInterviewState } from '../types';

interface PatientInterviewPanelProps {
  patientState: PatientInterviewState | null;
  targetCategories?: string[];
}

const DEFAULT_LEVEL_KEYS = ['Lv1', 'Lv2', 'Lv3', 'Lv4', 'Lv5', 'Lv6'];

const SPECIAL_LEVEL_ORDER: Record<string, number> = {
  proposal: 500,
  acceptance_intent: 600,
};

const KIND_LABELS: Record<string, string> = {
  fact: '事実',
  measurement: '数量',
  method: '方法',
  reason: '背景',
  goal: '提案',
  commitment: '受容',
  legacy: '旧Lv',
};

const C0_LEVEL_LABELS: Record<string, string> = {
  Lv1: '挨拶',
  Lv2: '自己',
  Lv3: '氏名',
  Lv4: '生年',
  Lv5: '同意',
  Lv6: '共感',
};

function levelKeySortValue(levelKey: string): number {
  const match = /^Lv(\d+)/.exec(levelKey);
  return match ? Number(match[1]) : (SPECIAL_LEVEL_ORDER[levelKey] ?? 999);
}

function sortLevelKeys(levelKeys: Iterable<string>): string[] {
  return Array.from(new Set(levelKeys)).sort((a, b) => {
    const numericDiff = levelKeySortValue(a) - levelKeySortValue(b);
    if (numericDiff !== 0) return numericDiff;
    return a.localeCompare(b);
  });
}

// C0（コミュニケーション）の段階名。他カテゴリの Lv には付けない
function levelDisplayLabel(levelKey: string, categoryId?: string): string {
  if (categoryId && canonicalCategoryId(categoryId) === 'C0' && C0_LEVEL_LABELS[levelKey]) return C0_LEVEL_LABELS[levelKey];
  if (levelKey === 'proposal') return '提案';
  if (levelKey === 'acceptance_intent') return '受容';
  return levelKey;
}

function canonicalCategoryId(categoryId: string): string {
  return categoryId.replace(/^SRC_/, '');
}

function buildTargetCategorySet(targetCategories?: string[]): Set<string> | null {
  if (!targetCategories || targetCategories.length === 0) return null;
  const ids = new Set<string>();
  for (const categoryId of targetCategories) {
    ids.add(categoryId);
    ids.add(canonicalCategoryId(categoryId));
  }
  return ids;
}

function isInstructionTargetCategory(categoryId: string, targetCategorySet: Set<string> | null): boolean {
  if (!targetCategorySet || categoryId === 'C0') return false;
  return targetCategorySet.has(categoryId) || targetCategorySet.has(canonicalCategoryId(categoryId));
}

function isCapturedItem(item: DentistItemSlot): boolean {
  return item.filled && Boolean(item.value) && item.value !== 'N/A';
}

function getLevelItems(patientState: PatientInterviewState, categoryId: string, levelKey: string): DentistItemSlot[] {
  return Object.values(patientState.itemSlots?.[categoryId] ?? {})
    .filter((item) => item.level === levelKey && item.value !== 'N/A');
}

function itemDotClass(item: DentistItemSlot): string {
  if (item.value === 'N/A') return 'border-gray-400 bg-gray-800 text-white';
  if (isCapturedItem(item)) {
    if (item.kind === 'goal') return 'border-emerald-300 bg-emerald-500 text-white';
    if (item.kind === 'commitment') return 'border-purple-300 bg-purple-500 text-white';
    return 'border-blue-300 bg-blue-500 text-white';
  }
  if (item.reviewRequired) return 'border-rose-300 bg-rose-50 text-rose-700';
  if (item.kind === 'goal') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (item.kind === 'commitment') return 'border-purple-200 bg-purple-50 text-purple-700';
  return 'border-gray-300 bg-gray-100 text-gray-600';
}

function extractMarker(value: string | undefined): string | null {
  const marker = value?.match(/[①-⑳㉑-㉟㊱-㊿]/)?.[0];
  if (marker) return marker;
  const suffix = value?.match(/_(\d{2})$/)?.[1];
  return suffix ? String(Number(suffix)) : null;
}

function itemChipLabel(item: DentistItemSlot, index: number): string {
  return extractMarker(item.label)
    ?? extractMarker(item.columnKey)
    ?? ((item.dimension === 'hygienist_proposal' ? '提' : item.dimension === 'patient_acceptance' ? '受' : '')
      || String(index + 1));
}

function itemDisplayName(item: DentistItemSlot): string {
  return item.label || item.sourceHeader || item.columnKey || item.itemKey;
}

function itemStatusLabel(item: DentistItemSlot): string {
  if (item.value === 'N/A') return '対象外';
  if (isCapturedItem(item)) return '取得';
  return '未取得';
}

function ItemSlotHoverTooltip({
  categoryId,
  levelLabel,
  item,
  desc,
  tableVal,
  align = 'left',
}: {
  categoryId: string;
  levelLabel: string;
  item: DentistItemSlot;
  desc?: string;
  tableVal?: string;
  align?: 'left' | 'center';
}) {
  const positionClass = align === 'center'
    ? 'right-3 top-24'
    : 'right-3 top-20';
  const captured = isCapturedItem(item);

  return (
    <div className={`fixed ${positionClass} hidden group-hover:block z-[9999] pointer-events-none`}>
      <div className="max-h-[60vh] w-[380px] max-w-[calc(100vw-24px)] overflow-y-auto rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
        <div className="font-semibold">
          {categoryId} {levelLabel} / {itemDisplayName(item)} [{itemStatusLabel(item)}]
        </div>
        <div className="font-mono text-[10px] text-gray-400 break-all">{item.itemKey}</div>
        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-gray-300">
          <span>kind: {item.kind ?? '-'}</span>
          <span>dimension: {item.dimension || '-'}</span>
          {item.pairKey && <span className="text-emerald-300">pair: {item.pairKey}</span>}
          {item.groupKey && <span className="text-emerald-300">group: {item.groupKey}</span>}
        </div>
        {desc && <div className="mt-1 whitespace-normal text-gray-300">{desc}</div>}
        {tableVal && <div className="mt-1 whitespace-normal text-cyan-300">期待値: {tableVal}</div>}
        <div className="mt-1 border-t border-gray-700 pt-1 whitespace-normal">
          <div className={captured ? 'text-blue-300' : 'text-gray-300'}>
            <span className="font-semibold">{itemStatusLabel(item)}</span>
            <span> / 期待値: </span>
            <span>{item.text || '(空)'}</span>
          </div>
          {captured && item.value && <div className="text-blue-300">取得値: {item.value}</div>}
          {item.sourceText && <div className="text-gray-300">根拠: {item.sourceText}</div>}
          {item.reviewRequired && <div className="text-rose-300">reviewRequired</div>}
        </div>
      </div>
    </div>
  );
}

function itemLevelDisplayLabel(levelKey: string, items: DentistItemSlot[], categoryId?: string): string {
  const kind = items.find((item) => item.kind)?.kind;
  if (kind && KIND_LABELS[kind]) return KIND_LABELS[kind];
  return levelDisplayLabel(levelKey, categoryId);
}

function getCategoryLevelKeys(patientState: PatientInterviewState, categoryId: string): string[] {
  const slot = patientState.slots?.[categoryId];
  const keys = new Set<string>();

  Object.keys(slot?.filledLevels ?? {}).forEach((key) => keys.add(key));
  Object.keys(patientState.levelAggregates?.[categoryId] ?? {}).forEach((key) => keys.add(key));
  Object.values(patientState.itemSlots?.[categoryId] ?? {}).forEach((item) => keys.add(item.level));
  Object.keys(patientState.rubricDescriptions?.[categoryId] ?? {}).forEach((key) => keys.add(key));
  Object.keys(patientState.tableValues?.[categoryId] ?? {}).forEach((key) => keys.add(key));
  Object.keys((patientState as any).echoSlots?.[categoryId] ?? {}).forEach((key) => keys.add(key));
  (patientState.naLevelMap?.[categoryId] ?? []).forEach((key) => keys.add(key));

  return keys.size > 0 ? sortLevelKeys(keys) : DEFAULT_LEVEL_KEYS;
}

export default function PatientInterviewPanel({ patientState, targetCategories: targetCategoriesProp }: PatientInterviewPanelProps) {
  // targetCategories: props優先、なければpatientStateから取得
  const targetCategories = buildTargetCategorySet(targetCategoriesProp ?? patientState?.targetCategories);

  if (!patientState) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs w-full space-y-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">患者情報取得状況</span>
          <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-xs">待機中</span>
        </div>
        <div className="text-gray-400">面接開始後に取得対象カテゴリを表示します。</div>
        {targetCategories && (
          <div className="flex items-center gap-3 text-gray-400 flex-wrap">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border border-emerald-300 bg-emerald-50 inline-block" />課題文の得点範囲
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border border-pink-300 bg-pink-50 inline-block" />コミュニケーション
            </span>
          </div>
        )}
      </div>
    );
  }

  const slots = Object.values(patientState.slots);
  const naCategories = patientState.naCategories ?? [];
  const naLevelMap = patientState.naLevelMap ?? {};
  const hasItemSlots = Object.keys(patientState.itemSlots ?? {}).length > 0;

  // N/A / 充填 / 未取得 の集計
  let countNA = 0;
  let countFilled = 0;
  let countEmpty = 0;
  for (const slot of slots) {
    const isFullNA = naCategories.includes(slot.categoryId);
    const naLevels = naLevelMap[slot.categoryId] ?? [];
    const categoryItems = Object.values(patientState.itemSlots?.[slot.categoryId] ?? {})
      .filter((item) => item.value !== 'N/A');

    if (categoryItems.length > 0) {
      for (const item of categoryItems) {
        if (isCapturedItem(item)) {
          countFilled++;
        } else {
          countEmpty++;
        }
      }
      continue;
    }

    for (const lvKey of getCategoryLevelKeys(patientState, slot.categoryId)) {
      if (isFullNA || naLevels.includes(lvKey)) {
        countNA++;
      } else {
        const val = slot.filledLevels?.[lvKey];
        if ((val ?? '') !== '') {
          countFilled++;
        } else {
          countEmpty++;
        }
      }
    }
  }
  const totalLevels = countNA + countFilled + countEmpty;
  const progressDenominator = Math.max(totalLevels - countNA, 1);
  const barDenominator = Math.max(totalLevels, 1);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs w-full space-y-2">
      {/* ヘッダー */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-600">患者情報取得状況</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          patientState.phase === 'done' ? 'bg-gray-100 text-gray-600' :
          'bg-blue-100 text-blue-800'
        }`}>
          {patientState.phase === 'done' ? '完了' : '問診中'}
        </span>
        <span className="ml-auto text-gray-400">ターン: {patientState.turnCount}</span>
        <span className="text-gray-400">Categories: {slots.length}</span>
      </div>

      {/* 進捗バー（N/A除外） */}
      <div>
        <div className="flex justify-between text-gray-500 mb-1">
          <span>スロット充填</span>
          <span>{countFilled}/{totalLevels - countNA} ({Math.round(countFilled / progressDenominator * 100)}%)</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 flex overflow-hidden">
          {countNA > 0 && (
            <div className="bg-gray-400 h-1.5 transition-all" style={{ width: `${countNA / barDenominator * 100}%` }} />
          )}
          {countFilled > 0 && (
            <div className="bg-blue-500 h-1.5 transition-all" style={{ width: `${countFilled / barDenominator * 100}%` }} />
          )}
        </div>
      </div>

      {/* カテゴリグリッド */}
      <div className="flex flex-wrap gap-1">
        {slots.map(slot => {
          const isCategoryNA = naCategories.includes(slot.categoryId);
          const isTargetCategory = isInstructionTargetCategory(slot.categoryId, targetCategories);
          const categoryHasItems = Object.keys(patientState.itemSlots?.[slot.categoryId] ?? {}).length > 0;
          const levelKeys = getCategoryLevelKeys(patientState, slot.categoryId)
            .filter((levelKey) => {
              if (isCategoryNA || (naLevelMap[slot.categoryId] ?? []).includes(levelKey)) return false;
              if (hasItemSlots && categoryHasItems) {
                return getLevelItems(patientState, slot.categoryId, levelKey).length > 0;
              }
              return true;
            });
          const activeLevelKeys = levelKeys.filter((levelKey) =>
            !isCategoryNA && !(naLevelMap[slot.categoryId] ?? []).includes(levelKey)
          );
          const categoryItems = Object.values(patientState.itemSlots?.[slot.categoryId] ?? {})
            .filter((item) => item.value !== 'N/A');
          const filledLevelCount = categoryItems.length > 0
            ? categoryItems.filter(isCapturedItem).length
            : activeLevelKeys.filter((levelKey) => (slot.filledLevels?.[levelKey] ?? '') !== '').length;
          const totalCount = categoryItems.length > 0 ? categoryItems.length : activeLevelKeys.length;

          if (hasItemSlots) {
            return (
              <div
                key={slot.categoryId}
                title={`${slot.categoryId}: ${slot.category}\n充填: ${filledLevelCount}/${totalCount}${isCategoryNA ? ' [N/A]' : ''}${isTargetCategory ? ' [課題文の得点範囲]' : ''}`}
                className={`
                  flex w-full items-start gap-2 rounded border px-2 py-1.5
                  ${isCategoryNA
                    ? 'border-gray-300 bg-gray-50'
                    : slot.categoryId === 'C0'
                      ? (slot.filled ? 'border-pink-300 bg-pink-50' : 'border-pink-200 bg-pink-50/50')
                      : isTargetCategory
                        ? (slot.filled ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-200 bg-emerald-50/50')
                        : (slot.filled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white')}
                `}
              >
                <div className="w-28 shrink-0">
                  <div className="font-mono text-xs font-semibold leading-tight text-gray-700">{slot.categoryId}</div>
                  <div className="truncate text-[10px] leading-tight text-gray-500">{slot.category}</div>
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                  {levelKeys.map(lvKey => {
                    const val = slot.filledLevels?.[lvKey];
                    const isFilled = (val ?? '') !== '';
                    const isEchoed = !!patientState.echoSlots?.[slot.categoryId]?.[lvKey];
                    const desc = patientState.rubricDescriptions?.[slot.categoryId]?.[lvKey] ?? '';
                    const tableVal = patientState.tableValues?.[slot.categoryId]?.[lvKey] ?? '';
                    const levelItems = getLevelItems(patientState, slot.categoryId, lvKey);

                    if (levelItems.length > 0) {
                      const levelLabel = itemLevelDisplayLabel(lvKey, levelItems, slot.categoryId);
                      return (
                        <div
                          key={lvKey}
                          className={`flex items-center gap-0.5 rounded border px-1 py-0.5 ${
                            isTargetCategory ? 'border-emerald-100 bg-emerald-50/70' : 'border-gray-100 bg-white'
                          }`}
                        >
                          <span className="mr-0.5 min-w-[24px] text-[10px] font-semibold leading-none text-gray-500">
                            {levelLabel}
                          </span>
                          {levelItems.map((item, itemIndex) => (
                            <span
                              key={item.itemKey}
                              className="relative inline-flex group"
                            >
                              <span
                                className={`h-3 min-w-[14px] rounded-[3px] border px-0.5 text-center text-[9px] font-bold leading-[10px] ${itemDotClass(item)}`}
                              >
                                {itemChipLabel(item, itemIndex)}
                              </span>
                              <ItemSlotHoverTooltip
                                categoryId={slot.categoryId}
                                levelLabel={levelLabel}
                                item={item}
                                desc={desc}
                                tableVal={tableVal}
                              />
                            </span>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <div key={lvKey} className="relative group flex items-center gap-0.5 rounded border border-gray-100 bg-white px-1 py-0.5">
                        <span className="mr-0.5 min-w-[24px] text-[10px] font-semibold leading-none text-gray-500">
                          {levelDisplayLabel(lvKey, slot.categoryId)}
                        </span>
                        <div
                          className={`w-2.5 h-2.5 rounded-sm ${
                            isEchoed
                              ? 'bg-green-500'
                              : (isFilled ? 'bg-blue-500' : 'bg-gray-300')
                          }`}
                        />
                        <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50 pointer-events-none">
                          <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap max-w-[280px]">
                            <div className="font-semibold">
                              {slot.categoryId} {levelDisplayLabel(lvKey, slot.categoryId)} [{isEchoed ? '復唱済み' : isFilled ? '取得済み' : '未取得'}]
                            </div>
                            {desc && <div className="text-gray-300 whitespace-normal">{desc}</div>}
                            {tableVal && <div className="text-cyan-300 whitespace-normal">期待値: {tableVal}</div>}
                            {isFilled && val && <div className="text-blue-300 whitespace-normal">取得値: {val}</div>}
                            {isEchoed && <div className="text-green-300">復唱確認済み</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="w-12 shrink-0 text-right text-[10px] font-semibold text-gray-500">
                  {filledLevelCount}/{totalCount}
                </div>
              </div>
            );
          }

          return (
            <div
              key={slot.categoryId}
              title={`${slot.categoryId}: ${slot.category}\n充填: ${filledLevelCount}/${totalCount}${naCategories.includes(slot.categoryId) ? ' [N/A]' : ''}${isTargetCategory ? ' [課題文の得点範囲]' : ''}`}
              className={`
                flex flex-col items-center rounded p-1 cursor-default min-w-[36px]
                ${isCategoryNA
                  ? 'border border-gray-300 bg-gray-50'
                  : slot.categoryId === 'C0'
                    ? (slot.filled ? 'bg-pink-50 border border-pink-300' : 'border border-pink-200 bg-pink-50/50')
                    : isTargetCategory
                      ? (slot.filled ? 'bg-emerald-50 border border-emerald-300' : 'border border-emerald-200 bg-emerald-50/50')
                      : (slot.filled ? 'bg-blue-50 border border-blue-200' : 'border border-transparent')}
              `}
            >
            <span className="text-gray-500 leading-none mb-0.5 text-xs">{slot.categoryId}</span>
            <div className="flex gap-0.5">
              {levelKeys.map(lvKey => {
                const val = slot.filledLevels?.[lvKey];
                const isFilled = (val ?? '') !== '';
                const isLevelUnavailable = (naLevelMap[slot.categoryId] ?? []).includes(lvKey);
                const isEchoed = !!(patientState as any).echoSlots?.[slot.categoryId]?.[lvKey];
                const desc = patientState.rubricDescriptions?.[slot.categoryId]?.[lvKey] ?? '';
                const tableVal = patientState.tableValues?.[slot.categoryId]?.[lvKey] ?? '';
                const levelItems = getLevelItems(patientState, slot.categoryId, lvKey);
                const status = isCategoryNA
                  ? 'カテゴリN/A'
                  : isLevelUnavailable
                    ? 'レベル未定義'
                    : isEchoed
                      ? '復唱済み'
                      : (isFilled ? '取得済み' : '未取得');

                if (levelItems.length > 0) {
                  const levelLabel = itemLevelDisplayLabel(lvKey, levelItems, slot.categoryId);
                  return (
                    <div key={lvKey} className="flex flex-col items-center gap-0.5">
                      <span className="max-w-[28px] truncate text-[9px] leading-none text-gray-400">
                        {levelLabel}
                      </span>
                      <div className="flex max-w-[76px] flex-wrap justify-center gap-0.5">
                        {levelItems.map((item, itemIndex) => (
                          <span
                            key={item.itemKey}
                            className="relative inline-flex group"
                          >
                            <span
                              className={`h-3 min-w-[14px] rounded-[3px] border px-0.5 text-center text-[9px] font-bold leading-[10px] ${itemDotClass(item)}`}
                            >
                              {itemChipLabel(item, itemIndex)}
                            </span>
                            <ItemSlotHoverTooltip
                              categoryId={slot.categoryId}
                              levelLabel={levelLabel}
                              item={item}
                              desc={desc}
                              tableVal={tableVal}
                              align="center"
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={lvKey} className="relative group">
                    <div
                      className={`w-2.5 h-2.5 rounded-sm cursor-pointer ${
                        isCategoryNA
                          ? 'bg-gray-800'
                          : isLevelUnavailable
                            ? 'bg-gray-500'
                            : isEchoed
                              ? 'bg-green-500'
                              : (isFilled ? 'bg-blue-500' : 'bg-gray-300')
                      }`}
                    />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-50 pointer-events-none">
                      <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap max-w-[280px]">
                        <div className="font-semibold">{slot.categoryId} {levelDisplayLabel(lvKey, slot.categoryId)} [{status}]</div>
                        {desc && <div className="text-gray-300 whitespace-normal">{desc}</div>}
                        {tableVal && <div className="text-cyan-300 whitespace-normal">期待値: {tableVal}</div>}
                        {isFilled && val && <div className="text-blue-300 whitespace-normal">取得値: {val}</div>}
                        {isEchoed && <div className="text-green-300">復唱確認済み</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      {/* 凡例 */}
      <div className="flex items-center gap-3 text-gray-400 flex-wrap">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-blue-500 inline-block" />取得済み
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-green-500 inline-block" />復唱済み
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-gray-300 inline-block" />未取得
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-gray-500 inline-block" />レベル未定義
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-gray-800 inline-block" />N/A
        </span>
        {targetCategories && (
          <>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border border-emerald-300 bg-emerald-50 inline-block" />課題文の得点範囲
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border border-pink-300 bg-pink-50 inline-block" />コミュニケーション
            </span>
          </>
        )}
      </div>
    </div>
  );
}
